import React, { useState } from "react";

/**
 * CuvrIQ – Beta Overview Landing Page (Invite Only)
 * Sidebar layout: logo + nav links on the left, content on the right.
 * Pure React + vanilla CSS (scoped in <style>), NO Tailwind, NO icon libs.
 * Expects:
 *   - /public/cuvriq-logo.png
 *   - Serverless API: POST /api/beta-invite  -> inserts into Supabase `beta_invites`
 */

// Brand tokens
const BRAND_PRIMARY = "#0F172A"; // deep navy
const BRAND_ACCENT = "#06B6D4"; // teal
const BRAND_ACCENT_DARK = "#0AA4BD"; // hover/active
const TEXT = "#0B1220";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";
const BG_LIGHT = "#F8FAFC";

/* ---------- Minimal inline SVG icon system ---------- */
const Icon = ({ path, size = 18, color = "currentColor" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    style={{ flex: "0 0 auto" }}
  >
    <path d={path} />
  </svg>
);

const icons = {
  brain: "M12 3c-2.8 0-5 2.2-5 5v1H6a3 3 0 100 6h1v1a5 5 0 005 5m0-18c2.8 0 5 2.2 5 5v1h1a3 3 0 110 6h-1v1a5 5 0 01-5 5",
  upload: "M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M16 6l-4-4-4 4M12 2v14",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  search: "M21 21l-5.2-5.2M3 10a7 7 0 1114 0 7 7 0 01-14 0z",
  file: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z",
  chart: "M4 19h16M8 13v6M12 9v10M16 5v14",
  zap: "M13 2L3 14h9l-1 8L21 10h-9l1-8z",
  users: "M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M9 7a4 4 0 110-8 4 4 0 010 8z",
  lock: "M7 11V7a5 5 0 0110 0v4M6 11h12v10H6V11z",
  star: "M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z",
  arrowRight: "M5 12h14M12 5l7 7-7 7",
  external: "M18 13v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3",
  check: "M20 6L9 17l-5-5",
};

/* --------------------------------- Content --------------------------------- */
const features = [
  {
    icon: <Icon path={icons.brain} size={20} />,
    title: "Riq – AI Coverage Assistant",
    desc: "Ask natural-language questions like ‘Does this plan cover SRP?’ and get clear, data-backed answers in seconds.",
  },
  {
    icon: <Icon path={icons.upload} size={20} />,
    title: "Upload & Parse Plans",
    desc: "Drag-and-drop PDFs or images. Multi-page merge, AI parsing, and structured plan data ready to verify.",
  },
  {
    icon: <Icon path={icons.shield} size={20} />,
    title: "HIPAA-Safe by Design",
    desc: "PHI is never stored. Stored data is plan-level only — never patient info. Uploaded document is only temporarily stored, deleted after parsing",
  },
  {
    icon: <Icon path={icons.search} size={20} />,
    title: "Smart Lookup",
    desc: "Find plans by employer or group ID. Fuzzy matching and plan selection memory make follow-ups effortless.",
  },
  {
    icon: <Icon path={icons.file} size={20} />,
    title: "Verify & View",
    desc: "Inline editing on Verify, a clean read-only Plan Viewer.",
  },
  {
    icon: <Icon path={icons.chart} size={20} />,
    title: "Metrics Dashboard",
    desc: "Track plans uploaded, verified, and Riq interaction accuracy so you can see impact at a glance.",
  },
];

const valueProps = [
  { icon: <Icon path={icons.zap} />, text: "Instant answers to your coverage questions. No need to verify plans that other offices have already verified and shared here." },
  { icon: <Icon path={icons.users} />, text: "No insurance expertise required" },
  { icon: <Icon path={icons.lock} />, text: "Zero-PHI storage model with strict security controls" },
  { icon: <Icon path={icons.star} />, text: "Affordable by design — no bloated enterprise price tags" },
];

const steps = [
  {
    step: "01",
    title: "Upload & Redact",
    desc: "Drop in benefit breakdowns. PHI information is disregarded and not parsed.",
  },
  {
    step: "02",
    title: "Parse & Verify",
    desc: "We extract structured coverage (preventive, basic, major, perio, ortho) with notes and flags. You confirm.",
  },
  {
    step: "03",
    title: "Ask Riq Anything",
    desc: "Use natural language to get precise, plan-backed answers. Riq remembers your selected plan context.",
  },
  {
    step: "04",
    title: "Share & Scale",
    desc: "Reuse verified plans instead of re-verifying from scratch. Save hours in wasted time re-verifying what others already have verified.",
  },
];

const faqs = [
  {
    q: "Who is a good fit for the beta?",
    a: "Solo or small group dental practices that want faster, clearer insurance answers without complex installs. Admins, front office teams, and clinicians are all welcome. Since this is an early access, beta offering, you may encounter occassional bugs that should be reported to us for fine tuning.",
  },
  {
    q: 'What does "beta" mean here?',
    a: "You’re getting early access to core features while we polish user interface, test features, and fine-tune Riq. We’ll ask for periodic feedback to shape what ships next.",
  },
  {
    q: "Is patient data stored?",
    a: "No. Our system is designed to strip PHI before anything is saved. CuvrIQ stores plan-level coverage details only. Uploaded files are deleted right after our AI extracts relevant information.",
  },
  {
    q: "How much time will setup take?",
    a: "Minutes. Create an account, upload a few plan documents, verify the extracted data, and you’re ready to ask Riq.",
  },
  {
    q: "What will it cost after beta?",
    a: "We’re targeting an affordable monthly subscription tailored to small practices. Beta pricing and incentives will be shared with accepted offices.",
  },
  {
    q: "Do we need to switch practice management software?",
    a: "No. CuvrIQ runs alongside your current tools. You do not need any integrations or modifications to your current practice management software.",
  },
];

/* ------------------------------- Subcomponents ------------------------------ */
function SectionHeader({ kicker, title, subtitle }) {
  return (
    <div className="section-header">
      {kicker && <p className="kicker">{kicker}</p>}
      <h2 className="section-title">{title}</h2>
      {subtitle && <p className="section-subtitle">{subtitle}</p>}
    </div>
  );
}

function LogoLockup({ className = "logo" }) {
  return (
    <div className="logo-wrap">
      <img src="/cuvriq-logo.png" alt="CuvrIQ logo" className={className} />
    </div>
  );
}

/* ---------------------------------- Page ----------------------------------- */
export default function CuvrIQBeta() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    role: "",
    practice: "",
    size: "",
    email: "",
    phone: "",
    location: "",
    software: "",
    why: "",
    agree: false,
  });

  async function submit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (!form.name || !form.email || !form.practice) {
      setError("Please enter your name, email, and practice.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!form.agree) {
      setError("Please agree to be contacted about the beta.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/beta-invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSuccess(true);
      setForm({
        name: "",
        role: "",
        practice: "",
        size: "",
        email: "",
        phone: "",
        location: "",
        software: "",
        why: "",
        agree: false,
      });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="page" id="top">
      {/* Scoped styles */}
      <style>{`
        :root {
          --brand-primary: ${BRAND_PRIMARY};
          --brand-accent: ${BRAND_ACCENT};
          --brand-accent-dark: ${BRAND_ACCENT_DARK};
          --text: ${TEXT};
          --muted: ${MUTED};
          --border: ${BORDER};
          --bg-light: ${BG_LIGHT};
        }
        * { box-sizing: border-box; }

        .page {
          min-height: 100vh;
          color: var(--text);
          background: #fff;
        }

        /* Layout */
        .layout {
          display: flex;
          min-height: 100vh;
        }

        .sidebar {
          width: 320px;
          flex-shrink: 0;
          border-right: 1px solid var(--border);
          padding: 20px 18px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          background: #ffffff;
          position: sticky;
          top: 0;
          align-self: flex-start;
          height: 100vh;
        }

        .sidebar-logo-area {
          display: flex;
          flex-direction: column;
          gap: 6px;
          align-items: center;
        }

        .logo-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          justify-content: center;
        }

        /* Bigger logo so tagline text is readable */
        .logo {
          height: auto;
          width: 320px;
          display: block;
        }


        .sidebar-nav {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-top: 8px;
        }

        .sidebar-nav a {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 6px;
          padding: 8px 10px;
          border-radius: 10px;
          font-size: 14px;
          color: #4b5563;
          text-decoration: none;
        }

        .sidebar-nav a span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }

        .sidebar-nav a small {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: #9ca3af;
        }

        .sidebar-nav a:hover {
          background: #f3f4f6;
          color: var(--brand-primary);
        }

        .sidebar-nav a.active {
          background: #e0f2fe;
          color: var(--brand-primary);
        }

        .sidebar-cta {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border-radius: 12px;
          padding: 10px 14px;
          font-size: 14px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          border: 1px solid transparent;
        }

        .btn-primary {
          background: var(--brand-primary);
          color: #fff;
          border-color: var(--brand-primary);
        }

        .btn-primary:hover {
          background: var(--brand-accent);
          border-color: var(--brand-accent);
        }

        .btn-outline {
          background: #fff;
          color: var(--brand-primary);
          border-color: var(--border);
        }

        .btn-outline:hover {
          background: #f3f4f6;
        }

        .main {
          flex: 1;
          padding: 28px 24px 40px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .container {
          max-width: 850px;
          margin: 0 auto;
        }

        /* Sections */
        .section {
          padding: 40px 0;
        }

        .hero {
          padding-top: 12px;
        }

        .hero-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.1fr);
          gap: 36px;
          align-items: center;
        }

        .kicker {
          text-transform: uppercase;
          letter-spacing: 0.12em;
          font-weight: 700;
          font-size: 12px;
          color: var(--brand-accent);
        }

        .hero-title {
          margin-top: 12px;
          font-weight: 900;
          line-height: 1.1;
          font-size: 40px;
          color: var(--brand-primary);
        }

        .hero-sub {
          margin-top: 14px;
          color: var(--muted);
          font-size: 18px;
        }

        .hero-ctas {
          margin-top: 18px;
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .value-grid {
          margin-top: 16px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          font-size: 14px;
        }

        .value-item {
          display: flex;
          gap: 8px;
          align-items: flex-start;
          color: #374151;
        }

        /* Preview cards */
        .preview {
          background: linear-gradient(135deg, #fff, #f6f7fb);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 24px;
          box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
        }

        .card {
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 16px;
          background: #fff;
        }

        .card + .card {
          margin-top: 16px;
        }

        .card-title {
          font-weight: 700;
          color: var(--brand-primary);
        }

        .card-desc {
          margin-top: 4px;
        }

        .live {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #64748b;
          font-size: 12px;
          margin-bottom: 8px;
        }

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: var(--brand-accent);
        }

        /* Section headers */
        .section-header {
          max-width: 720px;
          margin: 0 auto;
          text-align: center;
        }

        .section-title {
          font-size: 30px;
          font-weight: 800;
          color: var(--brand-primary);
          line-height: 1.2;
        }

        .section-subtitle {
          color: #6b7280;
          font-size: 16px;
          margin-top: 8px;
        }

        /* Feature grid */
        .muted-section {
          background: var(--bg-light);
          border-radius: 24px;
          padding: 32px 24px;
        }

        .features-grid {
          margin-top: 28px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .feature {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 18px;
          transition: box-shadow 0.2s ease;
        }

        .feature:hover {
          box-shadow: 0 6px 18px rgba(2, 6, 23, 0.06);
        }

        .feature-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: grid;
          place-items: center;
          color: #fff;
          background: var(--brand-primary);
          margin-bottom: 8px;
        }

        .feature-title {
          font-weight: 700;
          color: var(--brand-primary);
          font-size: 16px;
        }

        .feature-desc {
          color: #64748b;
          font-size: 14px;
          margin-top: 4px;
          line-height: 1.5;
        }

        /* Steps */
        .steps-grid {
          margin-top: 24px;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
        }

        .step {
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 16px;
        }

        .step-num {
          color: #64748b;
          font-weight: 700;
          font-size: 12px;
        }

        .step-title {
          margin-top: 6px;
          font-weight: 700;
          color: var(--brand-primary);
          font-size: 15px;
        }

        .step-desc {
          margin-top: 6px;
          color: #6b7280;
          font-size: 14px;
        }

        /* Security / cards */
        .security-grid {
          margin-top: 24px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }

        .security-card {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 18px;
        }

        .security-title {
          display: flex;
          gap: 8px;
          align-items: center;
          color: var(--brand-primary);
          font-weight: 700;
          font-size: 15px;
        }

        .security-desc {
          margin-top: 8px;
          color: #6b7280;
          font-size: 14px;
        }

        /* Beta expectations */
        .cols-3 {
          margin-top: 24px;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 16px;
        }

        .list {
          color: #64748b;
          font-size: 14px;
          margin-top: 10px;
          padding-left: 18px;
        }

        .list li {
          margin-bottom: 6px;
        }

        /* FAQ */
        .faq-grid {
          margin-top: 24px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .faq-card {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 16px;
          padding: 18px;
        }

        .faq-q {
          font-weight: 700;
          color: var(--brand-primary);
        }

        .faq-a {
          margin-top: 8px;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.6;
        }

        /* Form */
        .form {
          margin-top: 24px;
          display: grid;
          gap: 12px;
        }

        .grid-2 {
          display: grid;
          gap: 12px;
          grid-template-columns: 1fr 1fr;
        }

        label {
          font-size: 14px;
          font-weight: 600;
          display: block;
          margin-bottom: 4px;
        }

        input[type="text"],
        input[type="email"],
        textarea {
          width: 100%;
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 10px 12px;
          font-size: 14px;
        }

        textarea {
          min-height: 96px;
          resize: vertical;
        }

        .checkbox {
          display: flex;
          gap: 8px;
          align-items: flex-start;
          font-size: 14px;
          color: #6b7280;
        }

        .alert {
          border-radius: 12px;
          padding: 10px 12px;
          font-size: 14px;
        }

        .alert.error {
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #b91c1c;
        }

        .alert.ok {
          border: 1px solid #bbf7d0;
          background: #f0fdf4;
          color: #166534;
        }

        /* Footer */
        .footer {
          border-top: 1px solid var(--border);
          margin-top: 40px;
          padding-top: 18px;
        }

        .footer-inner {
          display: flex;
          flex-direction: row;
          gap: 12px;
          align-items: center;
          justify-content: space-between;
          color: #6b7280;
          font-size: 14px;
        }

        .footer-links {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .footer a {
          color: inherit;
          text-decoration: none;
        }

        .footer a:hover {
          color: #0f172a;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .sidebar {
            position: relative;
            height: auto;
          }
        }

        @media (max-width: 900px) {
          .layout {
            flex-direction: column;
          }

          .sidebar {
            width: 100%;
            height: auto;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 12px 16px;
            border-right: none;
            border-bottom: 1px solid var(--border);
          }

          .sidebar-logo-area {
            flex-direction: row;
            align-items: center;
            gap: 8px;
          }

          .sidebar-tagline {
            display: none;
          }

          /* Slightly larger than before on mobile, but still fits */
          .logo {
            width: 190px;
          }

          .sidebar-nav {
            flex-direction: row;
            gap: 4px;
            margin-top: 0;
            margin-left: 16px;
            flex-wrap: nowrap;
            overflow-x: auto;
          }

          .sidebar-nav a {
            padding: 6px 10px;
            font-size: 13px;
          }

          .sidebar-nav a small {
            display: none;
          }

          .sidebar-cta {
            flex-direction: row;
            margin-top: 0;
            margin-left: 16px;
          }

          .main {
            padding: 24px 16px 32px;
          }

          .hero-grid {
            grid-template-columns: minmax(0, 1fr);
          }

          .value-grid {
            grid-template-columns: 1fr;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .steps-grid {
            grid-template-columns: 1fr 1fr;
          }

          .security-grid {
            grid-template-columns: 1fr;
          }

          .cols-3 {
            grid-template-columns: 1fr;
          }

          .faq-grid {
            grid-template-columns: 1fr;
          }

          .grid-2 {
            grid-template-columns: 1fr;
          }

          .footer-inner {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <div className="layout">
        {/* Sidebar */}
        <aside className="sidebar">
          <div className="sidebar-logo-area">
            <LogoLockup />
            
          </div>

          <nav className="sidebar-nav">
            <a href="#top" className="active">
              <span>Overview</span>
            </a>
            <a href="#features">
              <span>Features</span>
            </a>
            <a href="#how">
              <span>How it works</span>
            </a>
            <a href="#security">
              <span>Security</span>
            </a>
            <a href="#faq">
              <span>FAQ</span>
            </a>
            <a href="#apply">
              <span>Request invite</span>
            </a>
          </nav>

          <div className="sidebar-cta">
            <a href="/cuvriq-video" className="btn btn-outline">
              <span>View demo</span>
              <Icon path={icons.external} size={15} />
            </a>
            <a href="#apply" className="btn btn-primary">
              <span>Request beta</span>
              <Icon path={icons.arrowRight} size={15} />
            </a>
          </div>
        </aside>

        {/* Main content */}
        <main className="main">
          <section className="section hero">
            <div className="container">
              <div className="hero-grid">
                <div>
                  <p className="kicker">Invite-only beta</p>
                  <h1 className="hero-title">Coverage questions. Intelligent answers.</h1>
                  <p className="hero-sub">
                    Ask simple questions about dental insurance and get instant answers.
                    Fluoride. Sealants. Orthodontic Benefits. Waiting periods. Frequencies. Missing tooth clauses.
                    Riq helps your team understand coverage in seconds.
                  </p>
                  <div className="hero-ctas">
                    <a href="#apply" className="btn btn-primary">
                      Request Beta Access <Icon path={icons.arrowRight} size={18} />
                    </a>
                    <a
                      href="/cuvriq-video"
                      className="btn btn-outline"
                      style={{ borderColor: BRAND_ACCENT, color: BRAND_ACCENT }}
                    >
                      See Cuvriq and Riq in action <Icon path={icons.external} size={18} />
                    </a>
                  </div>
                  <div className="value-grid">
                    {valueProps.map((v, i) => (
                      <div key={i} className="value-item">
                        <span style={{ color: BRAND_ACCENT }}>{v.icon}</span>
                        {v.text}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="preview">
                    <div className="card">
                      <div className="live">
                        <span className="dot" /> Live
                      </div>
                      <div className="card-title">Riq Chat</div>
                      <div className="card-desc" style={{ color: MUTED, fontSize: 14 }}>
                         <div><strong>Q:</strong> Does this plan cover adult fluoride?</div>
                          <div style={{ marginTop: "8px" }}>
                                <strong>A:</strong> Yes. Covered at 100% once every 12 months.
                          </div>
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-title">Verify Page</div>
                      <div className="card-desc" style={{ color: MUTED, fontSize: 14 }}>
                        Inline edits, procedure-level notes, flags for missing tooth clause, COB, and more.
                      </div>
                    </div>
                    <div className="card">
                      <div className="card-title">Plan Viewer</div>
                      <div className="card-desc" style={{ color: MUTED, fontSize: 14 }}>
                        Read-only, organized so you see the plan details the same way everytime.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Features */}
          <section id="features" className="section">
            <div className="container muted-section">
              <SectionHeader
                kicker="Core capabilities"
                title="Stop re-verifying plans that other offices have already verified"
                subtitle="Upload once. Verify once. Let Riq answer coverage questions instantly."
              />
              <div className="features-grid">
                {features.map((f, i) => (
                  <div key={i} className="feature">
                    <div className="feature-icon">{f.icon}</div>
                    <div className="feature-title">{f.title}</div>
                    <div className="feature-desc">{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How it Works */}
          <section id="how" className="section">
            <div className="container">
              <SectionHeader kicker="Workflow" title="From upload to answer in four simple steps" />
              <div className="steps-grid">
                {steps.map((s, i) => (
                  <div key={i} className="step">
                    <div className="step-num">{s.step}</div>
                    <div className="step-title">{s.title}</div>
                    <div className="step-desc">{s.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Security */}
          <section id="security" className="section">
            <div className="container muted-section">
              <SectionHeader
                kicker="Privacy & security"
                title="HIPAA-safe from the ground up"
                subtitle="CuvrIQ stores de-identified, plan-level data only. PHI is removed before storage and never used to answer questions."
              />
              <div className="security-grid">
                <div className="security-card">
                  <div className="security-title">
                    <Icon path={icons.shield} size={18} /> PHI Never stored
                  </div>
                  <div className="security-desc">
                    Automated exclusion of PHI prior to parse saves only what’s needed: insurer, plan details, coverage, waiting periods, notes, and
                    flags.
                  </div>
                </div>
                <div className="security-card">
                  <div className="security-title">
                    <Icon path={icons.lock} size={18} /> Secure Storage & Access
                  </div>
                  <div className="security-desc">
                    Role-based access, row-level security, and audit-friendly metadata for traceability and confidence status.
                  </div>
                </div>
                <div className="security-card">
                  <div className="security-title">
                    <Icon path={icons.check} size={18} /> Data-Backed Answers
                  </div>
                  <div className="security-desc">
                    Riq only answers from verified plan data. If something’s unclear, you’ll get a transparent explanation and suggested next
                    steps.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Beta Expectations */}
          <section className="section">
            <div className="container">
              <SectionHeader
                kicker="Beta program"
                title="What to expect if you’re accepted"
                subtitle="Short setup, real results, and a direct line to influence the roadmap."
              />
              <div className="cols-3">
                <div className="step">
                  <div className="step-title">What you’ll get</div>
                  <ul className="list">
                    <li>Access to the latest CuvrIQ beta features</li>
                    <li>Priority support and onboarding help</li>
                    <li>Invite to a private feedback channel</li>
                    <li>Beta pricing & incentives at launch</li>
                  </ul>
                </div>
                <div className="step">
                  <div className="step-title">What we’ll ask</div>
                  <ul className="list">
                    <li>Try CuvrIQ with real-day workflows</li>
                    <li>Share feedback on accuracy & user interface</li>
                    <li>Report issues with examples when possible</li>
                    <li>Optional: a short testimonial if you love it</li>
                  </ul>
                </div>
                <div className="step">
                  <div className="step-title">Who’s a fit</div>
                  <ul className="list">
                    <li>Solo & small-group practices</li>
                    <li>Teams that value speed & clarity</li>
                    <li>Early adopters comfortable with rapid updates</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="section">
            <div className="container muted-section">
              <SectionHeader kicker="Answering your questions" title="Beta FAQ" />
              <div className="faq-grid">
                {faqs.map((f, i) => (
                  <div key={i} className="faq-card">
                    <div className="faq-q">{f.q}</div>
                    <div className="faq-a">{f.a}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Apply */}
          <section id="apply" className="section">
            <div className="container">
              <SectionHeader
                kicker="Apply now"
                title="Request your CuvrIQ beta invite"
                subtitle="Tell us a bit about your practice. We’ll follow up by email with next steps."
              />
              <form onSubmit={submit} className="form">
                <div className="grid-2">
                  <div>
                    <label>Full name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="First and Last name"
                    />
                  </div>
                  <div>
                    <label>Role</label>
                    <input
                      type="text"
                      value={form.role}
                      onChange={(e) => setForm({ ...form, role: e.target.value })}
                      placeholder="Owner, Admin, Hygienist…"
                    />
                  </div>
                </div>
                <div className="grid-2">
                  <div>
                    <label>Practice name</label>
                    <input
                      type="text"
                      value={form.practice}
                      onChange={(e) => setForm({ ...form, practice: e.target.value })}
                      placeholder="Healthy Teeth Dental Care"
                    />
                  </div>
                  <div>
                    <label>Practice size</label>
                    <input
                      type="text"
                      value={form.size}
                      onChange={(e) => setForm({ ...form, size: e.target.value })}
                      placeholder="# of employees including providers"
                    />
                  </div>
                </div>
                <div className="grid-2">
                  <div>
                    <label>Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@practice.com"
                    />
                  </div>
                  <div>
                    <label>Phone (optional)</label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div className="grid-2">
                  <div>
                    <label>Location</label>
                    <input
                      type="text"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      placeholder="City, State"
                    />
                  </div>
                  <div>
                    <label>Practice software</label>
                    <input
                      type="text"
                      value={form.software}
                      onChange={(e) => setForm({ ...form, software: e.target.value })}
                      placeholder="Dentrix, Eaglesoft, Open Dental…"
                    />
                  </div>
                </div>
                <div>
                  <label>Why are you interested in CuvrIQ?</label>
                  <textarea
                    value={form.why}
                    onChange={(e) => setForm({ ...form, why: e.target.value })}
                    placeholder="What problems are you hoping to solve?"
                  />
                </div>
                <div className="checkbox">
                  <input
                    id="tos"
                    type="checkbox"
                    checked={form.agree}
                    onChange={(e) => setForm({ ...form, agree: e.target.checked })}
                  />
                  <label htmlFor="tos">
                    I agree to be contacted about the beta and understand that features may change rapidly during testing.
                  </label>
                </div>

                {error && <div className="alert error">{error}</div>}
                {success && <div className="alert ok">Thanks! Your request has been received. We'll email you next steps.</div>}

                <div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ opacity: loading ? 0.7 : 1 }}
                  >
                    {loading ? "Submitting…" : "Submit request"}
                  </button>
                </div>
              </form>
            </div>
          </section>

          {/* Footer */}
          <footer className="footer">
            <div className="container footer-inner">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <LogoLockup />
                <strong style={{ color: BRAND_PRIMARY }}>CuvrIQ</strong>
                <span>•</span>
                <span>Coverage questions. Intelligent answers.</span>
              </div>
              <div className="footer-links">
                <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                >
                Privacy
                </a>
                <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                >
                Terms
                </a>
                <a href="mailto:pmakadiadds@gmail.com">Contact</a>
              </div>

            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
